'use client';
import React, { createContext, useContext, useReducer, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ISystemUser from '@/types/SystemUser';
import { useSocket } from '@/providers/SocketContext';
import { useAuth } from '@/providers/AuthContext';
import { useChannelsQuery } from '@/hooks/useChannelsQuery';
import { useSystemUsers } from '@/hooks/useSystemUsers';
import { type GatesDeEstado, criaHandlersDeContato } from '@/lib/contacts/contactSocketHandlers';
import { checkPermission } from '@/lib/permissions/check';
import { CONTACTS_PERMISSIONS } from '@/lib/permissions/contacts';
import { useChannelRosterQuery } from '@/hooks/useChannelRosterQuery';
import { mergeChannelRoster } from '@/lib/channelRoster';
import type { AuthUser, ScopedChannel } from '@/types/auth';
import type { ChannelName, ChannelWithStats } from '@/types/channel';

export interface IChannelInfo {
  db_id: string;
  name: string;
  description: string;
  active: boolean;
  transmission_groups: number;
  active_chats: number;
  chats_count: number;
  /** Conversas com `unread_count > 0`. Subconjunto de `chats_count`, não partição. */
  waiting_chats: number;
  total_unread_messages: number;
  /** Slug lucide kebab; null = sem ícone. Só a query HTTP entrega — o /auth/me não. */
  icon: string | null;
  /** Hex #RRGGBB; null = sem cor. Só a query HTTP entrega — o /auth/me não. */
  color: string | null;
}

/**
 * Alvo de transferência/redirect: identidade vinda do ROSTER (tenant-wide) + aparência
 * vinda da lista ESCOPADA. Sem `phone_hash_id` de propósito — canal fora do escopo não
 * tem, e `''` seria mentira tipada.
 */
export interface IAvailableChannelsRedirect {
  db_id: string;
  name: string;
  description: string;
  icon: string | null;
  color: string | null;
}

type ChatBotContextType = {
  channels: IChannelInfo[];
  availableChannelsRedirect: IAvailableChannelsRedirect[];
  systemUsers: ISystemUser[];
  isLoadingFreshData: boolean; // Indica se ainda não há dados suficientes pra renderizar a UI (ver derivação abaixo)
};

type ChatBotAction = { type: 'SET_CHANNELS'; payload: IChannelInfo[] };

/**
 * Mapeia os canais escopados do /auth/me para IChannelInfo.
 * A lista escopada (auth) define quais canais existem; contadores/stats
 * de um `db_id` já presente em `existing` são preservados (chegaram antes via query HTTP).
 * `active` é sempre `true` — o /auth/me só escopa canais ativos pro usuário.
 */
export function mapScopedChannelsToChannelInfo(
  scoped: ScopedChannel[],
  existing: IChannelInfo[]
): IChannelInfo[] {
  const existingByDbId = new Map(existing.map((channel) => [channel.db_id, channel]));

  return scoped.map((channel) => {
    const previous = existingByDbId.get(channel.db_id);
    return {
      db_id: channel.db_id,
      name: channel.name,
      description: channel.description,
      active: true,
      transmission_groups: previous?.transmission_groups ?? 0,
      active_chats: previous?.active_chats ?? 0,
      chats_count: previous?.chats_count ?? 0,
      waiting_chats: previous?.waiting_chats ?? 0,
      total_unread_messages: previous?.total_unread_messages ?? 0,
      // ScopedChannel (o que o /auth/me devolve) não tem aparência: preservar a que
      // a query HTTP já trouxe, senão o avatar perderia a cor a cada refresh do auth.
      icon: previous?.icon ?? null,
      color: previous?.color ?? null,
    };
  });
}

/**
 * Faz merge defensivo dos contadores vindos da query HTTP (`useChannelsQuery`, GET /whatsapp/channels)
 * sobre a lista atual. A lista atual (definida pelo auth) é a fonte de identidade dos canais
 * (nome/descrição/active não são sobrescritos); o incoming só atualiza os TRÊS contadores
 * (`chats_count`/`waiting_chats`/`total_unread_messages`) de canais já conhecidos.
 * Canais que só existem no incoming são adicionados defensivamente (entram com defaults zerados
 * pros campos que a query não entrega).
 * Incoming vazio (ou ausente) NUNCA apaga a lista atual — retorna a mesma referência de `current`.
 */
export function mergeChannelStats(
  current: IChannelInfo[],
  incoming: ChannelWithStats[]
): IChannelInfo[] {
  if (!incoming || incoming.length === 0) {
    return current;
  }

  const incomingByDbId = new Map(incoming.map((channel) => [channel.db_id, channel]));

  const merged = current.map((channel) => {
    const update = incomingByDbId.get(channel.db_id);
    if (!update) return channel;
    return {
      ...channel,
      chats_count: update.chats_count,
      waiting_chats: update.waiting_chats,
      total_unread_messages: update.total_unread_messages,
      icon: update.icon,
      color: update.color,
    };
  });

  const currentDbIds = new Set(current.map((channel) => channel.db_id));
  const onlyInIncoming = incoming
    .filter((channel) => !currentDbIds.has(channel.db_id))
    .map((channel) => ({
      db_id: channel.db_id,
      name: channel.name,
      description: channel.description,
      active: true,
      transmission_groups: 0,
      active_chats: 0,
      chats_count: channel.chats_count,
      waiting_chats: channel.waiting_chats,
      total_unread_messages: channel.total_unread_messages,
      icon: channel.icon,
      color: channel.color,
    }));

  return [...merged, ...onlyInIncoming];
}

/**
 * Alvos de transferência: o ROSTER (todo canal do tenant, já ordenado por nome pelo back)
 * enriquecido com a aparência e a descrição que só a lista ESCOPADA entrega.
 *
 * A identidade (`db_id`, `name`) sai SEMPRE do roster, nunca do incoming — canal fora do
 * escopo não está no incoming, e essa era exatamente a razão de o botão sumir. Canal fora
 * do escopo sai com `icon`/`color` `null` e `description` `''`: o `ChannelAvatar` cai no
 * gradiente default e a descrição some (condicional no `TransferChannelModal`).
 */
export function buildRedirectTargets(
  roster: ChannelName[],
  scopedWithStats: ChannelWithStats[] | undefined
): IAvailableChannelsRedirect[] {
  const appearanceByDbId = new Map((scopedWithStats ?? []).map((channel) => [channel.db_id, channel]));

  return roster.map(({ db_id, name }) => {
    const appearance = appearanceByDbId.get(db_id);
    return {
      db_id,
      name,
      description: appearance?.description ?? '',
      icon: appearance?.icon ?? null,
      color: appearance?.color ?? null,
    };
  });
}

/**
 * O que este usuário PODE VER, por eixo de estado de contato — o gate do refetch de
 * `contact_state_changed` (`lib/contacts/contactSocketHandlers`).
 *
 * Pura e exportada porque a decisão é o que
 * erra caro, e testá-la não pode exigir montar provider.
 */
export function gatesDeEstadoDoUsuario(user: AuthUser | null): GatesDeEstado {
  const permissoes = user?.permissions ?? [];
  return {
    podeVerBloqueados: checkPermission(permissoes, CONTACTS_PERMISSIONS.BLOCKED_READ),
    podeVerDeletados: checkPermission(permissoes, CONTACTS_PERMISSIONS.DELETED_READ),
  };
}

const ChatBotContext = createContext<ChatBotContextType | undefined>(undefined);

interface IChatBotState {
  channels: IChannelInfo[];
}

const ChatBotReducer = (state: IChatBotState, action: ChatBotAction): IChatBotState => {
  switch (action.type) {
    case 'SET_CHANNELS':
      return {
        ...state,
        channels: action.payload,
      };
    default:
      return state;
  }
};

const initialState: IChatBotState = {
  channels: [],
};

export const ChatBotProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { socket, socketReady } = useSocket();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const channelsQuery = useChannelsQuery();
  const rosterQuery = useChannelRosterQuery();
  const { systemUsers } = useSystemUsers();

  const [state, dispatch] = useReducer(ChatBotReducer, initialState);

  // Ref para acessar a lista atual de canais dentro do effect de merge, evitando stale closure.
  const channelsRef = useRef<IChannelInfo[]>([]);

  useEffect(() => {
    channelsRef.current = state.channels;
  }, [state.channels]);

  // Fonte da lista de canais: /auth/me (o quê existe) + useChannelsQuery via HTTP (contadores/stats).
  // Roda no mount, a cada refresh do AuthProvider e a cada atualização da query de canais.
  useEffect(() => {
    if (!user) return;
    const fromAuth = mapScopedChannelsToChannelInfo(user.channels, channelsRef.current);
    dispatch({
      type: 'SET_CHANNELS',
      payload: mergeChannelStats(fromAuth, channelsQuery.data ?? []),
    });
  }, [user, channelsQuery.data]);

  // Memo pra não re-registrar os listeners a cada render: `user.permissions` só troca
  // de identidade quando o `/auth/me` responde.
  const gatesDeEstado = useMemo(() => gatesDeEstadoDoUsuario(user), [user]);

  // Socket: join-only na room do tenant + invalidação da query de contatos em deltas.
  // Contatos não vivem mais neste contexto — a fonte é HTTP (useContactsSearch); o socket
  // só sinaliza "algo mudou" pro React Query buscar de novo.
  useEffect(() => {
    if (!socket || !socketReady) return;

    socket.emit('enter_chatbot');

    const handlers = criaHandlersDeContato(queryClient, gatesDeEstado);
    socket.on('new_contact', handlers.novoContato);
    socket.on('contact_updated', handlers.contatoAtualizado);
    socket.on('contact_state_changed', handlers.estadoMudou);

    return () => {
      socket.off('new_contact', handlers.novoContato);
      socket.off('contact_updated', handlers.contatoAtualizado);
      socket.off('contact_state_changed', handlers.estadoMudou);
      socket.emit('leave_chatbot');
    };
  }, [socket, socketReady, queryClient, gatesDeEstado]);

  // Alvos de transferência: ROSTER (identidade, tenant-wide) + lista escopada (aparência).
  // O `?? []` cru sobre `channelsQuery.data` que morava aqui era o bug: uma falha da query
  // esvaziava a lista, o gate `useTransferTargets().length > 0` fechava e o botão sumia
  // PERMANENTEMENTE (o poll de 30s repete o mesmo erro). `mergeChannelRoster` degrada pro
  // escopado do /auth/me — síncrono do localStorage — em vez de pra vazio.
  const availableChannelsRedirect = useMemo<IAvailableChannelsRedirect[]>(
    () => buildRedirectTargets(mergeChannelRoster(rosterQuery.data, user?.channels), channelsQuery.data),
    [rosterQuery.data, user?.channels, channelsQuery.data]
  );

  // Contrato de UI: só mostrar "Nenhum canal" quando a query resolveu e veio vazia.
  // isLoading (v5) = pending inicial; hidratação do IndexedDB via setQueryData já o zera.
  const isLoadingFreshData = state.channels.length === 0 && channelsQuery.isLoading;

  const value = useMemo<ChatBotContextType>(
    () => ({
      channels: state.channels,
      availableChannelsRedirect,
      systemUsers,
      isLoadingFreshData,
    }),
    [state.channels, availableChannelsRedirect, systemUsers, isLoadingFreshData]
  );

  return (
    <ChatBotContext.Provider value={value}>
      {children}
    </ChatBotContext.Provider>
  );
}

export const useChatBot = () => {
  const context = useContext(ChatBotContext);
  if (context === undefined) {
    throw new Error('useChatBot must be used within a ChatBotProvider');
  }
  return context;
};
